import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ImagePlus, Search, X } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import { useProducts } from '../../context/ProductsContext';
import form from '../../styles/form.module.css';
import styles from './Events.module.css';

const empty = {
  name: '',
  emoji: '',
  sortOrder: 1,
  description: '',
  status: 'ACTIVE',
  pricingMode: 'NONE',
  discountPercent: '',
  couponCode: '',
  startsAtDate: '',
  startsAtTime: '00:00',
  endsAtDate: '',
  endsAtTime: '23:59',
  imageUrl: '',
  itemIds: [],
};

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const toLocalDate = (iso) => (iso ? iso.slice(0, 10) : '');
const toLocalTime = (iso, fallback) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toTimeString().slice(0, 5);
};

const toIsoLocal = (date, time) => {
  if (!date) return null;
  const t = time || '00:00';
  return `${date}T${t.length === 5 ? `${t}:00` : t}+05:30`;
};

export default function EventFormDrawer({ open, onClose, onSave, event, saving, error }) {
  const { products: catalog } = useProducts();
  const [data, setData] = useState(empty);
  const [query, setQuery] = useState('');
  const [hadBanner, setHadBanner] = useState(false);
  const bannerRef = useRef(null);
  const isEdit = !!event;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    if (event) {
      setHadBanner(!!event.imageUrl);
      setData({
        ...empty,
        ...event,
        discountPercent: event.discountPercent ?? '',
        couponCode: event.couponCode || '',
        startsAtDate: toLocalDate(event.startsAt),
        startsAtTime: toLocalTime(event.startsAt, '00:00'),
        endsAtDate: toLocalDate(event.endsAt),
        endsAtTime: toLocalTime(event.endsAt, '23:59'),
        itemIds: event.itemIds || [],
        imageFile: null,
        removeBanner: false,
      });
    } else {
      setHadBanner(false);
      setData(empty);
    }
  }, [event, open]);

  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const selected = useMemo(
    () => catalog.filter((p) => data.itemIds.includes(p.id)),
    [catalog, data.itemIds],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = catalog.filter((p) => p.status === 'ACTIVE');
    if (!q) return list;
    return list.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(q));
  }, [catalog, query]);

  const toggleItem = (id) => {
    setData((d) => ({
      ...d,
      itemIds: d.itemIds.includes(id) ? d.itemIds.filter((x) => x !== id) : [...d.itemIds, id],
    }));
  };

  const handleBanner = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/') || file.size > MAX_IMAGE_BYTES) return;
    const reader = new FileReader();
    reader.onload = () => setData((d) => ({ ...d, imageUrl: reader.result, imageFile: file }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...data,
      discountPercent: data.pricingMode === 'PERCENT' ? Number(data.discountPercent) || 0 : '',
      couponCode: data.pricingMode === 'COUPON' ? data.couponCode.trim().toUpperCase() : '',
      startsAt: toIsoLocal(data.startsAtDate, data.startsAtTime),
      endsAt: toIsoLocal(data.endsAtDate, data.endsAtTime),
      removeBanner: hadBanner && !data.imageUrl && !data.imageFile,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit event' : 'Create event'}
      subtitle="Festival list on the storefront — aisle category stays the same"
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || !data.name.trim()}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <AuthMessage>{error}</AuthMessage>

        <p className={form.label} style={{ marginBottom: 8 }}>Event banner</p>
        <div
          className={styles.bannerDropzone}
          onClick={() => bannerRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          {data.imageUrl ? (
            <>
              <img src={data.imageUrl} alt="Event banner" className={styles.bannerDropzoneImg} />
              <button
                type="button"
                className={styles.bannerRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  setData((d) => ({ ...d, imageUrl: '', imageFile: null }));
                }}
                aria-label="Remove banner"
              >
                <X size={12} />
              </button>
            </>
          ) : (
            <>
              <ImagePlus size={20} />
              <span>Upload banner image</span>
            </>
          )}
        </div>
        <input ref={bannerRef} type="file" accept="image/*" hidden onChange={handleBanner} />

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Event name</span>
            <input className={form.input} required value={data.name} onChange={set('name')} placeholder="e.g. Independence Day Sale" />
          </label>
          <label className={form.field} style={{ maxWidth: 90 }}>
            <span className={form.label}>Emoji</span>
            <input className={form.input} value={data.emoji} onChange={set('emoji')} placeholder="🇮🇳" />
          </label>
        </div>

        <label className={form.field}>
          <span className={form.label}>Description</span>
          <textarea
            className={form.textarea}
            value={data.description}
            onChange={set('description')}
            placeholder="Shown under the event on the customer home."
          />
        </label>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Starts on</span>
            <input className={form.input} type="date" required value={data.startsAtDate} onChange={set('startsAtDate')} />
          </label>
          <label className={form.field}>
            <span className={form.label}>Start time</span>
            <input className={form.input} type="time" value={data.startsAtTime} onChange={set('startsAtTime')} />
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Ends on</span>
            <input className={form.input} type="date" required value={data.endsAtDate} onChange={set('endsAtDate')} />
          </label>
          <label className={form.field}>
            <span className={form.label}>End time</span>
            <input className={form.input} type="time" value={data.endsAtTime} onChange={set('endsAtTime')} />
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Status</span>
            <select className={form.select} value={data.status} onChange={set('status')}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </label>
          <label className={form.field}>
            <span className={form.label}>Pricing mode</span>
            <select className={form.select} value={data.pricingMode} onChange={set('pricingMode')}>
              <option value="NONE">None — featured list</option>
              <option value="PERCENT">Percent off items</option>
              <option value="COUPON">Linked coupon</option>
            </select>
          </label>
        </div>

        {data.pricingMode === 'PERCENT' && (
          <>
            <label className={form.field}>
              <span className={form.label}>Discount percent</span>
              <input className={form.input} type="number" min="1" max="90" required value={data.discountPercent} onChange={set('discountPercent')} placeholder="10" />
            </label>
            <p className={`${form.hint} ${styles.modeHint}`}>
              Applied only to selected products while the event is live. Cannot be stacked with a coupon at checkout.
            </p>
          </>
        )}

        {data.pricingMode === 'COUPON' && (
          <>
            <label className={form.field}>
              <span className={form.label}>Coupon code</span>
              <input
                className={form.input}
                required
                value={data.couponCode}
                onChange={set('couponCode')}
                placeholder="FEST20"
                style={{ textTransform: 'uppercase' }}
              />
            </label>
            <p className={`${form.hint} ${styles.modeHint}`}>
              Customers still enter this code at checkout. Use an existing promotion code.
            </p>
          </>
        )}

        {data.pricingMode === 'NONE' && (
          <p className={`${form.hint} ${styles.modeHint}`}>
            Aisle prices stay as-is. The event is a storefront collection only.
          </p>
        )}

        <div className={styles.sectionLabel}>
          <p className={styles.sectionTitle}>Selected products</p>
          <span className={styles.sectionCount}>{data.itemIds.length} selected</span>
        </div>
        <p className={form.hint} style={{ marginBottom: 10 }}>
          Products keep their aisle category. This list is the event collection customers browse.
        </p>

        {selected.length > 0 && (
          <div className={styles.chipWrap}>
            {selected.map((p) => (
              <span key={p.id} className={styles.itemChip}>
                {p.name}
                <button type="button" onClick={() => toggleItem(p.id)} aria-label={`Remove ${p.name}`}>
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className={styles.searchBox}>
          <Search size={14} />
          <input placeholder="Search catalog by name or SKU…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>

        <div className={styles.picker}>
          {filtered.length === 0 ? (
            <p className={styles.pickerEmpty}>No products match.</p>
          ) : filtered.map((p) => {
            const on = data.itemIds.includes(p.id);
            return (
              <button
                type="button"
                key={p.id}
                className={`${styles.pickerRow} ${on ? styles.pickerRowOn : ''}`}
                onClick={() => toggleItem(p.id)}
              >
                <span className={styles.check}>{on && <Check size={11} strokeWidth={3} />}</span>
                <span className={styles.pickerThumb}>
                  {p.images?.[0]?.url ? <img src={p.images[0].url} alt="" /> : p.emoji || '🛒'}
                </span>
                <span className={styles.pickerInfo}>
                  <span className={styles.pickerName}>{p.name}</span>
                  <span className={styles.pickerMeta}>{p.sku} · aisle category unchanged</span>
                </span>
              </button>
            );
          })}
        </div>
      </form>
    </Drawer>
  );
}
